import clsx from 'clsx';
import { useParams } from 'react-router';

import { DAYS_FOR_COMPANY_STATS, ONE } from '@/constants';
import { Divider, Skeleton, Typography } from '@/ui';

import type { ReactNode } from 'react';

import Empty from '@/components/Company/Stats/Empty';
import GeneralStats from '@/components/Company/Stats/GeneralStats';
import { useCompany } from '@/hooks/useCompany';

interface SharedCompanyStatsProps {
  feedback?: ReactNode;
  /**
   * Renders without the standalone top spacing, for the collapsed dashboard
   * header row where the surrounding row already owns the vertical rhythm.
   * Applies to every branch so the loaded, loading, and empty states align.
   */
  isCompact?: boolean;
}

const SharedCompanyStats = ({
  feedback,
  isCompact = false,
}: SharedCompanyStatsProps) => {
  const { companyId = '' } = useParams();
  const { companyData, isCompanyLoading } = useCompany(companyId);

  if (isCompanyLoading) {
    return (
      <Skeleton
        animation="wave"
        className={clsx('h-50 w-full rounded-xl', { 'mt-8': !isCompact })}
        variant="rectangular"
      />
    );
  }

  if (!companyData) return null;

  const companyCreatedAt = new Date(companyData.createdAt);

  // if company is less than 30 days old, we show an empty state
  const available = new Date();
  available.setDate(available.getDate() - DAYS_FOR_COMPANY_STATS);

  if (available <= companyCreatedAt) return <Empty isCompact={isCompact} />;

  const monthAgo = new Date();
  monthAgo.setMonth(monthAgo.getMonth() - ONE);

  const isOldEnough = companyCreatedAt <= monthAgo;

  if (!isOldEnough) {
    return <Empty isCompact={isCompact} />;
  }

  return (
    <div className={clsx('flex flex-col', { 'pt-14': !isCompact })}>
      <Typography
        className="mb-2 flex items-center gap-2 text-cement-500"
        variant="h4"
      >
        Mento Engagement
      </Typography>
      <Typography className="mb-6" variant="body1">
        Metrics are based on employees who completed onboarding and have a Coach
        assigned
      </Typography>
      <div>
        <div className="flex h-full flex-col md:flex-row md:space-x-6">
          <GeneralStats />
          {feedback && (
            <>
              <Divider
                className="hidden h-50 lg:block"
                orientation="vertical"
              />
              {feedback}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedCompanyStats;
